import { createApi } from "@reduxjs/toolkit/query/react";
import * as logger from "common/logger";
import * as config from "config";
import { toCreatePersonRequest } from "domain/people";
import * as utils from "utils";

const stripSpaces = (obj) => {
  if (typeof obj === "string") return obj.trim();
  if (Array.isArray(obj)) return obj.map(stripSpaces);
  if (obj && typeof obj === "object")
    return Object.fromEntries(
      Object.entries(obj).map(([k, v]) => [k, stripSpaces(v)])
    );
  return obj;
};

const baseQuery = async (args, api, extraOptions) => {
  const state = api.getState();
  const impersonatedCompanyId = state.impersonation?.companyId;

  try {
    const response = await utils.authenticatedFetch(
      config.endpoints.staticData(args.url),
      {
        headers: {
          "Content-Type": "application/json",
        },
        method: args.method ?? "post",
        body: args.body,
      },
      impersonatedCompanyId
    );
    return {
      data: await response.json(),
    };
  } catch (e) {
    logger.exception(e);
    return {
      error: String(e),
    };
  }
};

export const staticDataApi = createApi({
  reducerPath: "staticDataApi",
  baseQuery: baseQuery,
  endpoints: (builder) => ({
    multiConfig: builder.query({
      query: (keys) => ({
        url: "retrieve-config",
        body: JSON.stringify({ keys }),
      }),
    }),
    config: builder.query({
      query: (name) => ({
        url: "retrieve-config",
        body: JSON.stringify({
          keys: [name],
        }),
      }),
      transformResponse: (response, _, arg) => response?.[arg] ?? null,
    }),
    configForCompany: builder.query({
      queryFn: async ({ name, companyId }) => {
        try {
          const response = await utils.authenticatedFetch(
            config.endpoints.staticData("retrieve-config"),
            {
              headers: {
                "Content-Type": "application/json",
              },
              method: "post",
              body: JSON.stringify({ keys: [name] }),
            },
            companyId
          );
          return {
            data: (await response.json())[name],
          };
        } catch (e) {
          logger.exception(e);
          return {
            error: String(e),
          };
        }
      },
    }),
    listConfig: builder.query({
      query: () => ({
        url: "config",
        method: "get",
      }),
    }),
    featureFlags: builder.query({
      query: () => ({
        url: "feature-flags",
        method: "get",
      }),
    }),
    listBrokers: builder.query({
      query: () => ({
        url: "list-brokers",
      }),
    }),
    retrieveAuthConfig: builder.query({
      queryFn: async () => {
        try {
          const result = await utils.plainFetch(
            config.endpoints.staticData("retrieve-auth-config"),
            null,
            {
              method: "post",
            }
          );
          const data = await result.json();
          return { data };
        } catch (e) {
          logger.exception(e);
          return {
            error: String(e),
          };
        }
      },
    }),
    retrieveSecTickers: builder.query({
      query: () => ({
        url: "retrieve-sec-tickers",
      }),
    }),
    retrieveNavigationConfig: builder.query({
      query: () => ({
        url: "retrieve-navigation-config",
      }),
    }),
    adminRetrieveNavigationConfig: builder.query({
      query: ({ companyId }) => ({
        url: "admin-retrieve-navigation-config",
        body: JSON.stringify({ companyId }),
      }),
    }),
    adminUpdateConfig: builder.mutation({
      query: ({ key, value }) => ({
        url: "admin-update-config",
        body: JSON.stringify({ key, value }),
      }),
    }),
    createBroker: builder.mutation({
      query: (broker) => ({
        url: "create-broker",
        body: JSON.stringify(stripSpaces(broker)),
      }),
    }),
    createPuniBroker: builder.mutation({
      query: (broker) => ({
        url: "create-puni-broker",
        body: JSON.stringify(stripSpaces(broker)),
      }),
    }),
    createRetailBroker: builder.mutation({
      query: (broker) => ({
        url: "create-retail-broker",
        body: JSON.stringify(stripSpaces(broker)),
      }),
    }),
    createSurplusBroker: builder.mutation({
      query: (broker) => ({
        url: "create-surplus-broker",
        body: JSON.stringify(stripSpaces(broker)),
      }),
    }),
    createProducerBroker: builder.mutation({
      query: (broker) => ({
        url: "create-producer-broker",
        body: JSON.stringify(stripSpaces(broker)),
      }),
    }),
    createWholesaleBroker: builder.mutation({
      query: (broker) => ({
        url: "create-wholesale-broker",
        body: JSON.stringify(stripSpaces(broker)),
      }),
    }),
    listAssistants: builder.query({
      query: () => ({
        url: "list-assistants",
      }),
    }),
    createAssistant: builder.mutation({
      query: (assistant) => ({
        url: "create-assistant",
        body: JSON.stringify(stripSpaces(assistant)),
      }),
    }),
    listPeople: builder.query({
      query: () => ({
        url: "list-people",
      }),
    }),
    createPerson: builder.mutation({
      query: (person) => ({
        url: "create-person",
        body: JSON.stringify(stripSpaces(person)),
      }),
    }),
    createConfiguredPerson: builder.mutation({
      query: (person) => ({
        url: "create-person",
        body: JSON.stringify(toCreatePersonRequest(stripSpaces(person))),
      }),
    }),
    updatePerson: builder.mutation({
      query: (person) => ({
        url: "update-person",
        body: JSON.stringify(stripSpaces(person)),
      }),
    }),
    deletePerson: builder.mutation({
      query: (personId) => ({
        url: "delete-person",
        body: JSON.stringify({ id: personId }),
      }),
    }),
  }),
});

export const {
  useListConfigQuery,
  useConfigQuery,
  useConfigForCompanyQuery,
  useLazyConfigQuery,
  useMultiConfigQuery,
  useFeatureFlagsQuery,
  useListBrokersQuery,
  useCreateBrokerMutation,
  useCreatePuniBrokerMutation,
  useCreateRetailBrokerMutation,
  useCreateSurplusBrokerMutation,
  useCreateProducerBrokerMutation,
  useCreateWholesaleBrokerMutation,
  useListAssistantsQuery,
  useCreateAssistantMutation,
  useListPeopleQuery,
  useCreatePersonMutation,
  useCreateConfiguredPersonMutation,
  useUpdatePersonMutation,
  useDeletePersonMutation,
  useRetrieveAuthConfigQuery,
  useRetrieveNavigationConfigQuery,
  useAdminRetrieveNavigationConfigQuery,
  useAdminUpdateConfigMutation,
  useRetrieveSecTickersQuery,
} = staticDataApi;

export const useFeatureFlag = (flag, { default: def } = {}) => {
  const { data: featureFlags } = useFeatureFlagsQuery();
  return featureFlags?.[flag] ?? def ?? false;
};
